import React, { useState, useRef, useEffect } from 'react';
import { X, Play, Pause, SkipBack, SkipForward, Volume2, VolumeX } from 'lucide-react';

const AudioPlayer = ({ audioUrl, title, fullText = '', onClose }) => {
  const audioRef = useRef(null);
  const textContainerRef = useRef(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [volume, setVolume] = useState(1);
  const [isMuted, setIsMuted] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [sentences, setSentences] = useState([]);
  const [currentSentenceIndex, setCurrentSentenceIndex] = useState(-1);

  // Split text into sentences on mount
  useEffect(() => {
    if (!fullText) return;

    // Split text into sentences using regex (period, question mark, exclamation)
    const sentenceArray = fullText
      .split(/([.!?]+\s+)/)
      .filter(s => s.trim().length > 0)
      .reduce((acc, curr, idx, arr) => {
        // Combine sentence with its punctuation
        if (idx % 2 === 0) {
          const sentence = curr + (arr[idx + 1] || '');
          if (sentence.trim()) {
            acc.push(sentence.trim());
          }
        }
        return acc;
      }, []);

    setSentences(sentenceArray);
  }, [fullText]);

  // Calculate sentence timing based on audio duration
  useEffect(() => {
    if (!duration || sentences.length === 0) return;

    // Update current sentence index based on playback position
    const updateSentenceIndex = () => {
      const audio = audioRef.current;
      if (!audio) return;

      const progress = audio.currentTime / duration;
      const index = Math.floor(progress * sentences.length);
      setCurrentSentenceIndex(Math.min(index, sentences.length - 1));
    };

    updateSentenceIndex();
  }, [currentTime, duration, sentences]);

  // Auto-scroll to current sentence
  useEffect(() => {
    if (currentSentenceIndex >= 0 && textContainerRef.current) {
      const highlightedElement = textContainerRef.current.querySelector(`[data-sentence-index="${currentSentenceIndex}"]`);
      if (highlightedElement) {
        highlightedElement.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }
  }, [currentSentenceIndex]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;

    const handleTimeUpdate = () => setCurrentTime(audio.currentTime);
    const handleLoadedMetadata = () => setDuration(audio.duration);
    const handleEnded = () => setIsPlaying(false);

    audio.addEventListener('timeupdate', handleTimeUpdate);
    audio.addEventListener('loadedmetadata', handleLoadedMetadata);
    audio.addEventListener('ended', handleEnded);

    return () => {
      audio.removeEventListener('timeupdate', handleTimeUpdate);
      audio.removeEventListener('loadedmetadata', handleLoadedMetadata);
      audio.removeEventListener('ended', handleEnded);
    };
  }, []);

  const togglePlay = () => {
    const audio = audioRef.current;
    if (isPlaying) {
      audio.pause();
    } else {
      audio.play();
    }
    setIsPlaying(!isPlaying);
  };

  const skip = (seconds) => {
    const audio = audioRef.current;
    audio.currentTime = Math.max(0, Math.min(audio.currentTime + seconds, duration));
  };

  const handleSeek = (e) => {
    const audio = audioRef.current;
    const bounds = e.currentTarget.getBoundingClientRect();
    const percent = (e.clientX - bounds.left) / bounds.width;
    audio.currentTime = percent * duration;
  };

  const toggleMute = () => {
    const audio = audioRef.current;
    audio.muted = !isMuted;
    setIsMuted(!isMuted);
  };

  const handleVolumeChange = (e) => {
    const newVolume = parseFloat(e.target.value);
    const audio = audioRef.current;
    audio.volume = newVolume;
    setVolume(newVolume);
    if (newVolume > 0 && isMuted) {
      setIsMuted(false);
      audio.muted = false;
    }
  };

  const changeSpeed = (speed) => {
    const audio = audioRef.current;
    audio.playbackRate = speed;
    setPlaybackSpeed(speed);
  };

  const formatTime = (seconds) => {
    if (!seconds || isNaN(seconds)) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/90 p-4 animate-fade-in overflow-hidden">
      <div className="bg-[#1A1A1A] max-w-5xl w-full h-[90vh] rounded-lg overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-white/10 flex-shrink-0">
          <div>
            <p className="text-xs text-[#D4AF37] uppercase tracking-wider mb-1">AI Audiobook</p>
            <h2 className="font-['Bebas_Neue'] text-2xl">{title}</h2>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/10 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        {/* Main Content - Text + Controls */}
        <div className="flex-1 overflow-hidden flex flex-col md:flex-row">
          {/* Text Display with Highlighting */}
          {sentences.length > 0 && (
            <div 
              ref={textContainerRef}
              className="flex-1 p-6 overflow-y-auto border-b md:border-b-0 md:border-r border-white/10"
            >
              <div className="prose prose-invert max-w-none">
                {sentences.map((sentence, index) => (
                  <span
                    key={`sentence-${index}-${sentence.slice(0, 20)}`}
                    data-sentence-index={index}
                    className={`transition-all duration-300 ${
                      index === currentSentenceIndex
                        ? 'bg-[#D4AF37] text-black px-1 rounded font-medium'
                        : 'text-[#A3A3A3]'
                    }`}
                  >
                    {sentence}{' '}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Player Controls */}
          <div className="w-full md:w-96 p-6 flex flex-col justify-between flex-shrink-0">
            {/* Audio Element */}
            <audio ref={audioRef} src={audioUrl} preload="metadata" />

            <div>
              {/* Progress Bar */}
              <div className="mb-6">
                <div
                  className="h-2 bg-white/10 rounded-full cursor-pointer overflow-hidden"
                  onClick={handleSeek}
                >
                  <div
                    className="h-full bg-[#D4AF37] transition-all"
                    style={{ width: `${(currentTime / duration) * 100 || 0}%` }}
                  />
                </div>
                <div className="flex justify-between text-xs text-[#A3A3A3] mt-2">
                  <span>{formatTime(currentTime)}</span>
                  <span>{formatTime(duration)}</span>
                </div>
              </div>

              {/* Main Controls */}
              <div className="flex items-center justify-center gap-4 mb-6">
                <button
                  onClick={() => skip(-15)}
                  className="p-3 hover:bg-white/10 rounded-full transition-colors"
                  title="Rewind 15s"
                >
                  <SkipBack size={24} />
                </button>

                <button
                  onClick={togglePlay}
                  className="p-6 bg-[#D4AF37] hover:bg-[#E5C048] rounded-full transition-colors"
                >
                  {isPlaying ? <Pause size={32} className="text-black" /> : <Play size={32} className="text-black" />}
                </button>

                <button
                  onClick={() => skip(15)}
                  className="p-3 hover:bg-white/10 rounded-full transition-colors"
                  title="Forward 15s"
                >
                  <SkipForward size={24} />
                </button>
              </div>

              {/* Bottom Controls */}
              <div className="space-y-4">
                {/* Playback Speed */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-[#A3A3A3] w-16">Speed:</span>
                  <div className="flex gap-1 flex-1 flex-wrap">
                    {[0.75, 1, 1.25, 1.5].map((speed) => (
                      <button
                        key={speed}
                        onClick={() => changeSpeed(speed)}
                        className={`px-3 py-1 text-xs rounded transition-colors ${
                          playbackSpeed === speed
                            ? 'bg-[#D4AF37] text-black'
                            : 'bg-white/10 text-white hover:bg-white/20'
                        }`}
                      >
                        {speed}x
                      </button>
                    ))}
                  </div>
                </div>

                {/* Volume Control */}
                <div className="flex items-center gap-3">
                  <button
                    onClick={toggleMute}
                    className="p-2 hover:bg-white/10 rounded-full transition-colors"
                  >
                    {isMuted || volume === 0 ? <VolumeX size={20} /> : <Volume2 size={20} />}
                  </button>
                  <input
                    type="range"
                    min="0"
                    max="1"
                    step="0.1"
                    value={volume}
                    onChange={handleVolumeChange}
                    className="flex-1 accent-[#D4AF37]"
                  />
                </div>
              </div>
            </div>

            {/* Info */}
            <div className="mt-6 p-4 bg-white/5 rounded text-sm text-[#A3A3A3]">
              <p>🎧 AI-powered text-to-speech</p>
              <p className="mt-1">✨ Follow along with highlighted text</p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AudioPlayer;
